/* Mats · Tarjeta de producto estándar — lógica global (una sola vez por página)
   Expone:
     window.MatsCart.add(variantId, quantity)   → Promise<cart>
     window.MatsCart.afterAdd(mode)            → 'drawer' | 'none' | 'cart'
     window.MatsWishlist.toggle(handle) / .has(handle) / .list()
   Delegación de eventos para:
     [data-mats-wish="handle"]       guardar / quitar
     [data-mats-add="variantId"]     añadir directo
     [data-mats-quick-view="url"]    abrir el quick view (o ir al producto si no existe) */
(() => {
  if (window.__matsCardInit) return;
  window.__matsCardInit = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const WISH_KEY = 'mats:wishlist';

  /* ---------- Live region ---------- */
  let live;
  const announce = (msg) => {
    if (!msg) return;
    if (!live) {
      live = document.createElement('p');
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;';
      document.body.appendChild(live);
    }
    live.textContent = '';
    requestAnimationFrame(() => { live.textContent = msg; });
  };

  /* ---------- Carrito ---------- */
  const MatsCart = {
    async add(id, quantity = 1) {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity }] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || matsT('card_add_error', 'No se ha podido añadir'));
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      this.notify(data, cart);
      return cart;
    },

    notify(added, cart) {
      const items = added.items || [added];
      const html = document.documentElement;
      // Eventos que escuchan Prestige / Impact (Maestrooo) + uno propio
      html.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items, cart } }));
      html.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
      html.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('mats:cart:add', { detail: { items, cart } }));
      document.querySelectorAll('[data-mats-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    },

    afterAdd(mode = 'drawer') {
      if (mode === 'cart') { window.location.href = `${root}cart`; return; }
      if (mode !== 'drawer') return;
      const drawer = document.querySelector('cart-drawer, #cart-drawer');
      if (!drawer) return;
      setTimeout(() => {
        if (drawer.open || drawer.hasAttribute('open')) return;
        if (typeof drawer.show === 'function') drawer.show();
        else drawer.setAttribute('open', '');
      }, 60);
    },

    announce,
  };

  /* ---------- Wishlist ---------- */
  const MatsWishlist = {
    list() {
      try { const v = JSON.parse(localStorage.getItem(WISH_KEY)); return Array.isArray(v) ? v : []; } catch (e) { return []; }
    },
    has(handle) { return this.list().includes(handle); },
    toggle(handle) {
      const list = this.list();
      const i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1); else list.push(handle);
      try { localStorage.setItem(WISH_KEY, JSON.stringify(list)); } catch (e) { /* modo privado */ }
      document.dispatchEvent(new CustomEvent('mats:wishlist:change', { detail: { handles: list } }));
      return list.includes(handle);
    },
    sync(scope = document) {
      const list = this.list();
      scope.querySelectorAll('[data-mats-wish]').forEach((b) => {
        b.setAttribute('aria-pressed', String(list.includes(b.dataset.matsWish)));
      });
      // Contador del icono de guardados (header)
      document.querySelectorAll('[data-mats-wish-count]').forEach((el) => {
        el.textContent = list.length;
        el.hidden = list.length === 0;
      });
    },
  };

  window.MatsCart = MatsCart;
  window.MatsWishlist = MatsWishlist;

  document.addEventListener('mats:wishlist:change', () => MatsWishlist.sync());
  document.addEventListener('shopify:section:load', () => MatsWishlist.sync());
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => MatsWishlist.sync());
  } else {
    MatsWishlist.sync();
  }

  /* ---------- Quick view bajo demanda ----------
     Si la sección mats-quick-view no está en theme.liquid, se pide al producto
     (?section_id=mats-quick-view) y se inserta en la página la primera vez. */
  const waitForElement = () => (customElements.get('mats-quick-view')
    ? Promise.resolve()
    : Promise.race([customElements.whenDefined('mats-quick-view'), new Promise((r) => setTimeout(r, 3000))]));

  let loadingModal = null;
  const ensureModal = async (url) => {
    let modal = document.querySelector('mats-quick-view');
    if (modal) return modal;
    if (!loadingModal) {
      loadingModal = (async () => {
        const clean = url.split('#')[0];
        const res = await fetch(`${clean}${clean.includes('?') ? '&' : '?'}section_id=mats-quick-view`, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`Quick view: HTTP ${res.status}`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = doc.querySelector('mats-quick-view');
        if (!el) throw new Error('Quick view: la sección mats-quick-view no existe en sections/');
        // Cargar CSS/JS del modal si aún no están
        doc.querySelectorAll('link[rel="stylesheet"]').forEach((l) => {
          if (![...document.styleSheets].some((s) => s.href === l.href)) document.head.appendChild(document.importNode(l, true));
        });
        doc.querySelectorAll('script[src]').forEach((s) => {
          if (!document.querySelector(`script[src="${s.getAttribute('src')}"]`)) {
            const n = document.createElement('script');
            n.src = s.getAttribute('src');
            n.defer = true;
            document.head.appendChild(n);
          }
        });
        const node = document.importNode(el, true);
        node.querySelectorAll('template[data-mqv-template]').forEach((tpl) => tpl.remove());
        document.body.appendChild(node);
        await waitForElement();
        const m = document.querySelector('mats-quick-view');
        if (m && m.cache) m.cache.set(clean, Promise.resolve(html));
        return m;
      })().catch((err) => { loadingModal = null; throw err; });
    }
    return loadingModal;
  };

  const openQuickView = async (url, trigger) => {
    try {
      trigger.classList.add('is-loading');
      const modal = await ensureModal(url);
      if (!modal || typeof modal.openFor !== 'function') throw new Error('Quick view: mats-quick-view.js no se ha cargado');
      modal.openFor(url, trigger);
    } catch (err) {
      console.warn('[Mats]', err.message);
      window.location.href = url;
    } finally {
      trigger.classList.remove('is-loading');
    }
  };

  /* ---------- Delegación ---------- */
  document.addEventListener('click', async (e) => {
    const wish = e.target.closest('[data-mats-wish]');
    if (wish) {
      e.preventDefault();
      MatsWishlist.toggle(wish.dataset.matsWish);
      return;
    }

    const qv = e.target.closest('[data-mats-quick-view]');
    if (qv) {
      e.preventDefault();
      openQuickView(qv.dataset.matsQuickView, qv);
      return;
    }

    const add = e.target.closest('[data-mats-add]');
    if (add && !add.disabled) {
      e.preventDefault();
      add.classList.add('is-loading');
      try {
        await MatsCart.add(add.dataset.matsAdd);
        add.classList.add('is-added');
        clearTimeout(add._t);
        add._t = setTimeout(() => add.classList.remove('is-added'), 1600);
        announce(add.dataset.matsAddedLabel || matsT('card_added', 'Añadido al carrito'));
        MatsCart.afterAdd(add.dataset.matsAfterAdd || 'drawer');
      } catch (err) {
        announce(err.message);
      } finally {
        add.classList.remove('is-loading');
      }
    }
  });
})();
